import React, { useMemo, useState } from "react";
import { ModalWrapper } from "./ModalWrapper";
import { POPUP_STYLES } from "../../constants/popupStyles";
import apiService from "../../services/api";
import { MeetingType } from "../../types/api";

// Ad-hoc meetings are limited to these meeting types (backend meeting_types.py):
// 4 = Case Manager Follow-Up, 3 = Psychiatric Follow-Up. Labels are taken from the
// meetingTypes data so they stay in sync with the names used everywhere else.
const AD_HOC_MEETING_TYPE_IDS = [4, 3];

const DEFAULT_LENGTH_MINUTES = 5;

interface Props {
  patientId: number;
  token: string;
  meetingTypes: MeetingType[];
  onClose: () => void;
  onSuccess: (meetingId?: number) => void;
  onError: (msg: string) => void;
}

// Build a "YYYY-MM-DDTHH:mm" string for the current local time, for the
// datetime-local input default.
function nowForInput(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export const AdHocMeetingModal: React.FC<Props> = ({ patientId, token, meetingTypes, onClose, onSuccess, onError }) => {
  // Build the type options from the shared meetingTypes data, falling back to the
  // known titles if a type isn't present in the list.
  const typeOptions = useMemo(() => {
    const fallback: Record<number, string> = { 4: "Case Manager Follow-Up", 3: "Psychiatric Follow-Up" };
    return AD_HOC_MEETING_TYPE_IDS.map((id) => ({
      id,
      label: meetingTypes.find((t) => t.id === id)?.name || fallback[id] || String(id),
    }));
  }, [meetingTypes]);

  const defaultStart = useMemo(nowForInput, []);
  const [start, setStart] = useState(defaultStart);
  const [meetingTypeId, setMeetingTypeId] = useState<number>(typeOptions[0].id);
  const [submitting, setSubmitting] = useState(false);

  const startMs = start ? new Date(start).getTime() : NaN;
  const canCreate = !submitting && !!start && !Number.isNaN(startMs);

  const handleCreate = async () => {
    if (!canCreate) return;
    setSubmitting(true);
    try {
      const res = await apiService.createAdHocMeeting({
        token,
        patient_id: patientId,
        meetingtype_id: meetingTypeId,
        start: startMs,
        length: DEFAULT_LENGTH_MINUTES,
      });
      onSuccess(res?.meeting_id);
      onClose();
    } catch (e: any) {
      onError(e?.message || "Failed to create meeting");
      onClose();
    }
  };

  return (
    <ModalWrapper modalId="AdHocMeetingModal" isOpen={true} onClose={onClose} title="Create Meeting" isLoading={submitting} width="max-w-sm">
      <div className="space-y-5">
        {/* Meeting start */}
        <div>
          <label className="block text-sm font-medium text-[#366A75] mb-2">Meeting Start</label>
          <input
            type="datetime-local"
            value={start}
            onChange={(e) => setStart(e.target.value)}
            className={POPUP_STYLES.input}
            disabled={submitting}
          />
        </div>

        {/* Type */}
        <div>
          <label className="block text-sm font-medium text-[#366A75] mb-2">Type</label>
          <select
            value={meetingTypeId}
            onChange={(e) => setMeetingTypeId(Number(e.target.value))}
            className={POPUP_STYLES.input}
            disabled={submitting}
          >
            {typeOptions.map((t) => (
              <option key={t.id} value={t.id}>
                {t.label}
              </option>
            ))}
          </select>
        </div>

        {/* Actions */}
        <div className="flex gap-3 justify-end pt-1">
          <button onClick={onClose} className={POPUP_STYLES.button} disabled={submitting}>
            Cancel
          </button>
          <button onClick={handleCreate} disabled={!canCreate} className={POPUP_STYLES.button}>
            {submitting ? "Creating…" : "Create"}
          </button>
        </div>
      </div>
    </ModalWrapper>
  );
};
